import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import {
  CreateEventResponse,
  RecordWithPlaintext,
  requestCreateEvent,
} from '@puzzlehq/sdk';
import { EventType } from '@puzzlehq/sdk';
import toast from 'react-hot-toast';
import { RecordCount } from '@src/hooks/usePurchaseHelper';
import { router } from '@src/providers/router';
import { LoadingStatus } from '@src/data/utils';

export enum Step {
  _01_View,
  _02_Confirmed,
}

type PurchaseProps = {
  programId: string;
  functionId: string;
  fee: number;
  price: number;
  skuId: number;
  recordCount: RecordCount;
  partPurchaseRecords: RecordWithPlaintext[];
};

type zPurchaseState = {
  status: LoadingStatus;
  step: Step;
  createEventResponse?: CreateEventResponse;
  close: () => void;
  purchase: (props: PurchaseProps) => Promise<CreateEventResponse>;
};

export const usePurchaseStore = create<zPurchaseState>()(
  immer((set) => ({
    status: 'idle',
    step: Step._01_View,
    close: async () => {
      await new Promise((f) => setTimeout(f, 500));
      set({
        status: 'idle',
        step: Step._01_View,
        createEventResponse: undefined,
      });
    },
    purchase: async ({
      programId,
      functionId,
      fee,
      price,
      skuId,
      recordCount,
      partPurchaseRecords,
    }: PurchaseProps) => {
      set({ status: 'loading' });
      try {
        if (recordCount === 'LOW')
          throw new Error('Not enough PART to purchase item');
        const recordCountNumber = Number(recordCount);
        if (partPurchaseRecords.length === 0) throw new Error('No records');
        if (partPurchaseRecords.length !== recordCountNumber)
          throw new Error('partPurchaseRecords.length !== recordCountNumber');

        const plaintexts = partPurchaseRecords.map((r) => r.plaintext);
        const response = await requestCreateEvent({
          fee,
          functionId,
          programId,
          type: EventType.StorePurchase,
          inputs: [`${skuId}u16`, ...plaintexts, `${price * 1_000_000}u64`],
          tokenIds: ['4255871388field'],
        });

        set({
          status: response.eventId ? 'succeeded' : 'failed',
          step: response.eventId ? Step._02_Confirmed : undefined,
          createEventResponse: response,
        });

        if (response.eventId) {
          router.invalidate();
        }

        if (response.error) {
          console.error('Error purchasing in response.error', response.error);
          toast.error(response.error);
        }
        return response;
      } catch (e) {
        set({ status: 'failed' });
        console.error('Error purchasing', e);
        toast.error(`Error Purchasing: ${(e as Error).message}`, {
          id: (e as Error).message,
        });
        return {
          error: (e as Error).message,
        } as CreateEventResponse;
      }
    },
  }))
);
